import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { devWarn } from "@/lib/log";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, MessageCircle, UserCheck, Activity } from "lucide-react";

const ICONS = {
  assigned: UserCheck,
  comment: MessageCircle,
  status_change: Activity,
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  // Deps intentionally empty — uses only stable references (api, setItems, setUnread, devWarn).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) {
      // Polling — don't spam the UI on transient failures; log for debugging.
      devWarn("notifications load failed:", e?.message || e);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 25000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleClickItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); }
      catch (e) { devWarn("mark-read failed:", e?.message || e); }
    }
    if (n.task_id && n.workspace_id) {
      navigate(`/workspace/${n.workspace_id}?task=${n.task_id}`);
    }
    load();
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch (e) {
      devWarn("mark-all-read failed:", e?.message || e);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative w-9 h-9 border border-[#E2E2E2] hover:border-[#0D0D0D] flex items-center justify-center transition-colors"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center leading-none"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="rounded-none border-[#0D0D0D] p-0 w-96" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E2E2]">
          <div className="font-mono text-xs uppercase tracking-[0.3em]">/ notifications</div>
          {items.length > 0 && (
            <button onClick={markAllRead} data-testid="notification-mark-all-read"
              className="text-[10px] font-mono uppercase tracking-wider hover:text-[#FF3B30]">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#5C5C5C]">You're all caught up.</div>
          ) : (
            items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button
                  key={n.id}
                  onClick={() => handleClickItem(n)}
                  data-testid={`notification-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-[#E2E2E2] last:border-b-0 flex items-start gap-3 hover:bg-[#F7F7F6] ${!n.read ? "bg-[#FFF7F6]" : ""}`}
                >
                  <span className={`mt-0.5 w-7 h-7 flex items-center justify-center ${!n.read ? "bg-[#FF3B30] text-white" : "bg-[#F0F0F0] text-[#5C5C5C]"}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-snug">{n.message}</p>
                    <p className="text-[10px] font-mono uppercase tracking-wider text-[#5C5C5C] mt-1">
                      {(n.created_at || "").slice(0, 16).replace("T", " ")}
                    </p>
                  </div>
                  {!n.read && <span className="w-1.5 h-1.5 mt-2 bg-[#FF3B30]" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
